/**
 * ChatSearchBar — inline search across the current conversation.
 *
 * Owner-spec 2026-05-31 (Phase 1 add-on): WhatsApp-style search inside
 * a single chat. Filters messages by substring (case-insensitive) and
 * exposes prev/next navigation between matches.
 *
 * Controlled by parent:
 *   open    — whether the bar is visible
 *   query   — current search string
 *   onQueryChange(s)
 *   onClose()
 *   matchCount    — total matches in the chat
 *   currentMatch  — 1-based index of focused match
 *   onPrev / onNext — move between matches
 *
 * The actual filtering + scroll-to-match lives in the parent so the
 * matched message can be highlighted in place; this component is just
 * the bar above the message list.
 */

import React, { useEffect, useRef } from 'react';

const ChatSearchBar = ({
  open,
  query,
  onQueryChange,
  onClose,
  matchCount,
  currentMatch,
  onPrev,
  onNext,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  if (!open) return null;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 10px',
      background: '#f6f6f6',
      borderBottom: '1px solid #ddd',
    }}>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => onQueryChange && onQueryChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Escape') onClose && onClose();
          else if (e.key === 'Enter') (e.shiftKey ? onPrev : onNext) && (e.shiftKey ? onPrev() : onNext());
        }}
        placeholder="חפש בצ'אט..."
        style={{
          flex: 1,
          padding: '6px 10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          fontSize: '13px',
        }}
      />

      <div style={{
        fontSize: '12px',
        color: '#666',
        minWidth: '54px',
        textAlign: 'center',
      }}>
        {query ? (matchCount > 0 ? `${currentMatch}/${matchCount}` : 'אין') : ''}
      </div>

      <button
        onClick={onPrev}
        disabled={!query || !matchCount}
        title="קודם"
        style={searchBtn(!query || !matchCount)}
      >
        ▲
      </button>
      <button
        onClick={onNext}
        disabled={!query || !matchCount}
        title="הבא"
        style={searchBtn(!query || !matchCount)}
      >
        ▼
      </button>
      <button
        onClick={onClose}
        title="סגור"
        style={searchBtn(false)}
      >
        ✕
      </button>
    </div>
  );
};

const searchBtn = (disabled) => ({
  background: 'none',
  border: 'none',
  fontSize: '14px',
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? '#bbb' : '#555',
  padding: '4px 8px',
});

export default ChatSearchBar;
